import UmlCompositeShape from '../../Types/UmlCompositeShape';
import PlantUmlParser from '../PlantUmlParser';
import PlantUmlMapper from '../PlantUmlMapper';

const SHAPE_GAP = 24; // horizontal gap between staggered top-level shapes, on top of each shape's own width

/**
 * Adapter between the generic import registry and the PlantUML parser/mapper
 * (Core/PlantUmlParser.js, Core/PlantUmlMapper.js). Unlike CsImportStrategy,
 * this does NOT do duplicate-filtering or package-growing (yet) — a paste is
 * always treated as a fresh batch of shapes.
 * @param {string} source
 * @param {number} x
 * @param {number} y
 * @param {import('../../Types/UmlClassShape').default[]} existingShapes - unused for now, kept for signature parity with runCsImport
 * @param {import('../../Types/UmlConnector').default[]} existingConnectors - unused for now, kept for signature parity
 * @param {{collapsed?: boolean}} [options] - unused for now, kept for signature parity
 * @returns {{shapes: object[], connectors: object[], warnings: string[]}}
 */
export function runPlantUmlImport(source, x, y, existingShapes = [], existingConnectors = [], options = {}) {
  const parser = new PlantUmlParser();
  const mapper = new PlantUmlMapper();
  const tokens = parser.parse(source);
  const { shapes, connectors } = mapper.toShapesAndConnectors(tokens);

  offsetTopLevelShapes(shapes, x, y);

  return { shapes, connectors, warnings: [] };
}

/**
 * The Mapper builds everything (top-level shapes AND their nested children)
 * at (0,0) — moving just the top-level shape via moveTo() leaves its children
 * stranded at (0,0), disconnected from the now-repositioned package (bug
 * found 2026-07-18: shapes existed in the data but rendered nowhere near
 * their package, all classes from every package stacked on literal (0,0)).
 * Same translateChildren pattern as UmlCanvasEngine.updateShapePosition,
 * duplicated here because that engine method only applies to shapes already
 * added to the canvas — these aren't yet at the point this strategy runs.
 * @param {object[]} shapes - top-level shapes only, all built at (0,0) by the Mapper
 * @param {number} x
 * @param {number} y
 */
function offsetTopLevelShapes(shapes, x, y) {
  let nextX = x;
  shapes.forEach(function offsetShape(shape) {
    const dx = nextX - shape.x;
    const dy = y - shape.y;
    shape.moveTo(nextX, y);
    if (shape instanceof UmlCompositeShape) {
      translateChildren(shape, dx, dy);
    }
    nextX += shape.w + SHAPE_GAP;
  });
}

/**
 * @param {import('../../Types/UmlCompositeShape').default} compositeShape
 * @param {number} dx
 * @param {number} dy
 */
function translateChildren(compositeShape, dx, dy) {
  compositeShape.children.forEach(function moveChild(child) {
    child.moveTo(child.x + dx, child.y + dy);
    if (child.caption) {
      child.caption.moveTo(child.caption.x + dx, child.caption.y + dy);
    }
    if (child instanceof UmlCompositeShape) {
      translateChildren(child, dx, dy);
    }
  });
}
